import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { Ticket, Client, User, Company } from '../api/supabaseEntities.js';
import { supabase } from '../api/supabaseClient.js';
import { Button } from "../components/ui/button.jsx";
import { Input } from "../components/ui/input.jsx";
import { Textarea } from "../components/ui/textarea.jsx";
import { Label } from "../components/ui/label.jsx";
import { Card, CardContent } from "../components/ui/card.jsx";
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { Combobox } from '../components/ui/combobox.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select.jsx";

export default function NewTicket() {
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [systems, setSystems] = useState([]);
  const [systemModules, setSystemModules] = useState([]);
  const navigate = useNavigate();

  const [ticket, setTicket] = useState({
    title: '',
    type: 'support',
    priority: 'medium',
    category: '',
    subcategory: '',
    description: '',
    company_id: '',
    client_id: '',
    status: 'open',
    call_type: 'produto',
    service_description: '',
    system_id: '',
    system_module_id: ''
  });

  // Configurações de status e prioridade (igual ao ViewTicket)
  const STATUS_CONFIG = {
    open: { label: 'Aberto', color: 'bg-blue-500', textColor: 'text-blue-700', bgColor: 'bg-blue-50' },
    analysis: { label: 'Em Análise', color: 'bg-yellow-500', textColor: 'text-yellow-700', bgColor: 'bg-yellow-50' },
    in_progress: { label: 'Em Andamento', color: 'bg-orange-500', textColor: 'text-orange-700', bgColor: 'bg-orange-50' },
    resolved: { label: 'Resolvido', color: 'bg-green-500', textColor: 'text-green-700', bgColor: 'bg-green-50' },
    closed: { label: 'Fechado', color: 'bg-gray-500', textColor: 'text-gray-700', bgColor: 'bg-gray-50' }
  };
  const PRIORITY_CONFIG = {
    low: { label: 'Baixa', color: 'bg-green-500', textColor: 'text-green-700', bgColor: 'bg-green-50' },
    normal: { label: 'Normal', color: 'bg-blue-500', textColor: 'text-blue-700', bgColor: 'bg-blue-50' },
    high: { label: 'Alta', color: 'bg-orange-500', textColor: 'text-orange-700', bgColor: 'bg-orange-50' },
    critical: { label: 'Crítica', color: 'bg-red-500', textColor: 'text-red-700', bgColor: 'bg-red-50' }
  };

  useEffect(() => {
    loadInitialData();
    loadUsersList();
  }, []);

  const loadClientsList = async () => {
    try {
      console.log('🔄 Carregando lista de clientes...');
      
      const clientsData = await Client.list();
      if (clientsData && clientsData.length > 0) {
        // Filtrar apenas clientes ativos
        const activeClients = clientsData.filter(client => 
          client.status === 'active' || !client.status
        );
        
        console.log('✅ Clientes carregados:', activeClients.length);
        setClients(activeClients);
      } else {
        console.warn('⚠️ Nenhum cliente encontrado');
        setClients([]);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar clientes:', error);
      setClients([]);
    }
  };

  const loadUsersList = async () => {
    try {
      const usersData = await User.list();
      const internalUsers = usersData.filter(u => u.role !== 'client');
      setUsers(internalUsers);
    } catch (error) {
      setUsers([]);
    }
  };

  const loadSystems = async (userData = null) => {
    const userForApi = userData || currentUser;
    try {
      console.log('🔄 Carregando sistemas via RPC...');
      
      // Usar RPC para buscar sistemas com autenticação customizada (mesma que funciona no Systems.jsx)
      const { data: rpcResult, error: rpcError } = await supabase.rpc('get_systems_with_custom_auth', {
        input_user_id: userForApi?.id || null // Para listagem geral de sistemas
      });
      
      console.log('📊 Resultado RPC systems:', { rpcResult, rpcError });
      
      if (rpcError) {
        console.warn('⚠️ RPC falhou, tentando query direta:', rpcError);
        
        // Fallback: query direta
        const { data: systemsData, error } = await supabase
          .from('systems')
          .select('id, name, description, version, status')
          .eq('status', 'active')
          .order('name');
        
        if (error) throw error;
        setSystems(systemsData || []);
        return;
      }
      
      if (!rpcResult || !rpcResult.success) {
        console.error('❌ RPC retornou sucesso=false:', rpcResult?.error);
        console.warn('⚠️ RPC falhou, tentando RPC administrativa...');
        
        // Fallback 1: Tentar RPC administrativa
        const { data: adminResult, error: adminError } = await supabase.rpc('get_systems_for_admin', {
          admin_user_id: userForApi?.id || null
        });
        
        if (!adminError && adminResult) {
          console.log(`✅ ${(adminResult || []).length} sistemas carregados via RPC administrativa:`, adminResult);
          setSystems(adminResult || []);
          return;
        }
        
        console.warn('⚠️ RPC administrativa também falhou, usando query direta...');
        
        // Fallback 2: query direta (mesmo fallback que já existia)
        const { data: systemsData, error } = await supabase
          .from('systems')
          .select('id, name, description, version, status')
          .eq('status', 'active')
          .order('name');
        
        if (error) throw error;
        setSystems(systemsData || []);
        return;
      }
      
      const systemsArray = rpcResult.data || [];
      console.log(`✅ ${systemsArray.length} sistemas carregados via RPC`);
      setSystems(systemsArray);
      
    } catch (error) {
      console.error('❌ Erro ao carregar sistemas:', error);
      setSystems([]);
    }
  };

  const loadSystemModules = async (systemId, userData = null) => {
    const userForApi = userData || currentUser;
    try {
      if (!systemId) {
        setSystemModules([]);
        return;
      }

      console.log('🔄 Carregando módulos via RPC para sistema:', systemId);
      
      // Usar RPC para buscar módulos com autenticação customizada (usando v2 que existe)
      const { data: rpcResult, error: rpcError } = await supabase.rpc('get_modules_with_custom_auth_v2', {
        input_user_id: userForApi?.id || null // Para listagem geral de módulos
      });
      
      console.log('📊 Resultado RPC modules:', { rpcResult, rpcError });
      
      if (rpcError) {
        console.warn('⚠️ RPC falhou, tentando query direta:', rpcError);
        
        // Fallback: query direta
        const { data: modulesData, error } = await supabase
          .from('system_modules')
          .select('id, name, description, module_type, is_core, system_id')
          .eq('system_id', systemId)
          .eq('status', 'active')
          .order('name');
        
        if (error) throw error;
        setSystemModules(modulesData || []);
        return;
      }
      
      if (!rpcResult || !rpcResult.success) {
        throw new Error(rpcResult?.error || 'Erro na RPC');
      }
      
      // Filtrar módulos pelo sistema selecionado
      const allModules = rpcResult.data || [];
      const systemModules = allModules.filter(module => module.system_id === systemId);
      console.log(`✅ ${systemModules.length} módulos carregados via RPC para sistema ${systemId}`);
      setSystemModules(systemModules);
      
    } catch (error) {
      console.error('❌ Erro ao carregar módulos:', error);
      setSystemModules([]);
    }
  };

  const loadInitialData = async () => {
    try {
      // Carregar usuário atual
      const userData = await User.me();
      setCurrentUser(userData);

      // Carregar sistemas após ter o usuário
      await loadSystems(userData);

      // Se for um cliente, já preenche o company_id e client_id
      if (userData.role === 'client') {
        setTicket(prev => ({
          ...prev,
          company_id: userData.company_id || '',
          client_id: userData.id
        }));
        console.log('👤 Cliente autenticado:', {
          company_id: userData.company_id,
          client_id: userData.id
        });
      } else {
        // Para usuários internos, carregar lista de clientes
        await loadClientsList();
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const handleClientChange = (clientId) => {
    if (clientId) {
      // Encontrar o cliente selecionado para pegar o company_id
      const selectedClient = clients.find(client => client.id === clientId);
      if (selectedClient) {
        setTicket(prev => ({
          ...prev,
          client_id: clientId,
          company_id: selectedClient.company_id || ''
        }));
        console.log('👤 Cliente selecionado:', {
          client_id: clientId,
          client_name: selectedClient.name,
          company_id: selectedClient.company_id
        });
      }
    } else {
      setTicket(prev => ({
        ...prev,
        client_id: '',
        company_id: ''
      }));
    }
  };

  const handleCallTypeChange = (callType) => {
    setTicket(prev => ({
      ...prev,
      call_type: callType,
      // Limpar campos específicos quando mudar o tipo
      service_description: callType === 'servico' ? prev.service_description : '',
      system_id: callType === 'produto' ? prev.system_id : '',
      system_module_id: callType === 'produto' ? prev.system_module_id : ''
    }));

    // Se mudou para serviço, limpar módulos
    if (callType === 'servico') {
      setSystemModules([]);
    }
  };

  const handleSystemChange = (systemId) => {
    setTicket(prev => ({
      ...prev,
      system_id: systemId,
      system_module_id: '' // Limpar módulo quando trocar sistema
    }));

    // Carregar módulos do sistema selecionado
    loadSystemModules(systemId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Garantir que temos o usuário atual
      if (!currentUser) {
        throw new Error('Usuário não encontrado');
      }

      // Para usuários internos, validar se cliente foi selecionado
      if (currentUser.role !== 'client' && !ticket.client_id) {
        alert('Por favor, selecione um cliente para o chamado.');
        setIsLoading(false);
        return;
      }

      // Preparar dados do ticket
      const ticketData = {
        ...ticket,
        requester_id: currentUser.id,
        company_id: ticket.company_id || null,
        client_id: ticket.client_id || null,
        assigned_to: ticket.assigned_to || null
      };

      console.log('🎫 Criando ticket:', ticketData);
      const createdTicket = await Ticket.create(ticketData);
      console.log('✅ Ticket criado com sucesso');

      // Enviar webhook se o ticket foi atribuído a um usuário
      if (ticketData.assigned_to) {
        try {
          console.log('📞 Verificando se usuário tem telefone para envio de webhook...');
          const assignedUser = await User.findById(ticketData.assigned_to);
          
          if (assignedUser && assignedUser.phone) {
            console.log('📱 Enviando webhook para usuário com telefone:', assignedUser.phone);
            
            const webhookPayload = {
              type: 'ticket_assigned',
              ticket: {
                id: createdTicket.id,
                title: ticketData.title,
                priority: ticketData.priority,
                status: ticketData.status,
                type: ticketData.type,
                created_at: new Date().toISOString()
              },
              assigned_user: {
                id: assignedUser.id,
                name: assignedUser.name,
                email: assignedUser.email,
                phone: assignedUser.phone
              },
              message: `Novo chamado atribuído: ${ticketData.title}`
            };

            await fetch('https://eoydy9430qifb0q.m.pipedream.net', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(webhookPayload)
            });

            console.log('✅ Webhook enviado com sucesso');
          } else {
            console.log('ℹ️ Usuário não possui telefone cadastrado, webhook não enviado');
          }
        } catch (webhookError) {
          console.error('❌ Erro ao enviar webhook:', webhookError);
          // Não interromper o fluxo principal por erro no webhook
        }
      }

      navigate(createPageUrl('Tickets'));
    } catch (error) {
      console.error('❌ Erro ao criar ticket:', error);
      alert('Erro ao criar chamado. Por favor, verifique os dados e tente novamente.\n\nDetalhes: ' + error.message);
    }
    setIsLoading(false);
  };

  if (!currentUser) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(createPageUrl('Tickets'))}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Novo Chamado</h1>
          <p className="text-muted-foreground mt-1">
            Crie um novo chamado no sistema
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="title">Título</Label>
                <Input
                  id="title"
                  placeholder="Digite o título do chamado"
                  value={ticket.title}
                  onChange={(e) => setTicket({ ...ticket, title: e.target.value })}
                  required
                />
              </div>

              {currentUser.role !== 'client' && (
                <div className="space-y-2">
                  <Label htmlFor="client">Cliente</Label>
                  <Combobox
                    id="client"
                    items={clients.map(c => ({ 
                      value: c.id, 
                      label: `${c.name || c.corporate_name || c.company_name}${c.company_name && c.company_name !== (c.name || c.corporate_name) ? ` (${c.company_name})` : ''}` 
                    }))}
                    value={ticket.client_id}
                    onChange={value => handleClientChange(value)}
                    placeholder="Pesquise o cliente pelo nome ou nome fantasia..."
                    required
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="type">Tipo</Label>
                <select
                  id="type"
                  value={ticket.type}
                  onChange={(e) => setTicket({ ...ticket, type: e.target.value })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="support">Suporte</option>
                  <option value="development">Desenvolvimento</option>
                  <option value="project">Projeto</option>
                  <option value="maintenance">Manutenção</option>
                  <option value="consultation">Consultoria</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <select
                  id="status"
                  value={ticket.status}
                  onChange={(e) => setTicket({ ...ticket, status: e.target.value })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <option key={key} value={key}>
                      {config.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority">Prioridade</Label>
                <select
                  id="priority"
                  value={ticket.priority}
                  onChange={(e) => setTicket({ ...ticket, priority: e.target.value })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                    <option key={key} value={key}>
                      {config.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Categoria</Label>
                <Input
                  id="category"
                  placeholder="Digite a categoria"
                  value={ticket.category}
                  onChange={(e) => setTicket({ ...ticket, category: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subcategory">Subcategoria</Label>
                <Input
                  id="subcategory"
                  placeholder="Digite a subcategoria"
                  value={ticket.subcategory}
                  onChange={(e) => setTicket({ ...ticket, subcategory: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="assigned_to">Responsável</Label>
                <Combobox
                  id="assigned_to"
                  items={users.map(u => ({ value: u.id, label: u.name }))}
                  value={ticket.assigned_to || ''}
                  onChange={value => setTicket(prev => ({ ...prev, assigned_to: value }))}
                  placeholder="Pesquise e selecione o responsável..."
                  allowClear
                />
              </div>

              {/* Tipo de Chamado */}
              <div className="space-y-2">
                <Label htmlFor="call_type">Tipo de Chamado</Label>
                <Select
                  value={ticket.call_type}
                  onValueChange={(value) => handleCallTypeChange(value)}
                >
                  <SelectTrigger>
                    <div className="flex items-center gap-2">
                      {ticket.call_type === 'servico' ? '🛠️ Serviço (Solicitação)' : '🖥️ Produto (Problema em Sistema)'}
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="produto">🖥️ Produto (Problema em Sistema)</SelectItem>
                    <SelectItem value="servico">🛠️ Serviço (Solicitação)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Campos condicionais para PRODUTO */}
              {ticket.call_type === 'produto' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="system_id">Sistema</Label>
                    <Select
                      value={ticket.system_id || ''}
                      onValueChange={value => handleSystemChange(value)}
                    >
                      <SelectTrigger>
                        <div className="flex items-center gap-2">
                          {systems.find(s => s.id === ticket.system_id)?.name || 'Selecione o sistema'}
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">Nenhum sistema</SelectItem>
                        {systems.map((system) => (
                          <SelectItem key={system.id} value={system.id}>
                            <div className="flex flex-col">
                              <span className="font-medium">{system.name}</span>
                              {system.version && (
                                <span className="text-xs text-muted-foreground">v{system.version}</span>
                              )}
                              {system.description && (
                                <span className="text-xs text-muted-foreground">{system.description}</span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {ticket.system_id && (
                    <div className="space-y-2">
                      <Label htmlFor="system_module_id">Módulo do Sistema</Label>
                      <Select
                        value={ticket.system_module_id || ''}
                        onValueChange={value => setTicket(prev => ({ ...prev, system_module_id: value }))}
                      >
                        <SelectTrigger>
                          <div className="flex items-center gap-2">
                            {systemModules.find(m => m.id === ticket.system_module_id)?.name || 'Selecione o módulo'}
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">Nenhum módulo específico</SelectItem>
                          {systemModules.map((module) => (
                            <SelectItem key={module.id} value={module.id}>
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {module.name} {module.is_core ? '(Core)' : ''}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {module.description || module.module_type}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Campo condicional para SERVIÇO */}
            {ticket.call_type === 'servico' && (
              <div className="space-y-2">
                <Label htmlFor="service_description">Descrição do Serviço Solicitado</Label>
                <Textarea
                  id="service_description"
                  placeholder="Descreva detalhadamente o serviço que está sendo solicitado..."
                  value={ticket.service_description}
                  onChange={(e) => setTicket({ ...ticket, service_description: e.target.value })}
                  className="min-h-[100px]"
                  required={ticket.call_type === 'servico'}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                placeholder="Descreva detalhadamente o chamado"
                value={ticket.description}
                onChange={(e) => setTicket({ ...ticket, description: e.target.value })}
                className="min-h-[150px]"
                required
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(createPageUrl('Tickets'))}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Criando...' : 'Criar Chamado'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
